import { LOGO_URL, currency, fmtDate } from "@/lib/api";

export default function AccountStatement({ statement }) {
  if (!statement) return null;
  const { customer, as_of, lines, buckets, total_owed } = statement;
  const b = buckets;

  const Bucket = ({ label, value, danger }) => (
    <div className="border border-slate-200 rounded-md p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider text-[#5B5C5F]">{label}</p>
      <p className={`font-mono-num font-semibold mt-1 ${danger ? "text-red-600" : "text-[#032352]"}`}>
        {currency(value)}
      </p>
    </div>
  );

  return (
    <div className="bg-white p-6 sm:p-8" data-testid="account-statement">
      <div className="flex justify-between items-start pb-4 border-b-2" style={{ borderColor: "#032352" }}>
        <img src={LOGO_URL} crossOrigin="anonymous" alt="J&L" className="h-16 object-contain" />
        <div className="text-right">
          <h2 className="font-heading text-2xl font-extrabold text-[#032352]">STATEMENT OF ACCOUNT</h2>
          <p className="text-sm text-[#5B5C5F]">As of {fmtDate(as_of)}</p>
        </div>
      </div>

      <div className="py-5">
        <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Account</p>
        <p className="font-semibold text-[#032352] text-lg">{customer.name}</p>
        {customer.address && (
          <p className="text-sm text-[#5B5C5F] whitespace-pre-line">{customer.address}</p>
        )}
        {customer.email && <p className="text-sm text-[#5B5C5F]">{customer.email}</p>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
        <Bucket label="Current" value={b.current} />
        <Bucket label="1–30 Days" value={b.d1_30} />
        <Bucket label="31–60 Days" value={b.d31_60} danger />
        <Bucket label="61–90 Days" value={b.d61_90} danger />
        <Bucket label="90+ Days" value={b.d90_plus} danger />
      </div>

      <table className="w-full">
        <thead>
          <tr style={{ backgroundColor: "#032352" }} className="text-white">
            <th className="p-2 text-left text-xs uppercase">Inv Date</th>
            <th className="p-2 text-left text-xs uppercase">Due Date</th>
            <th className="p-2 text-left text-xs uppercase">Invoice #</th>
            <th className="p-2 text-left text-xs uppercase">Order #</th>
            <th className="p-2 text-right text-xs uppercase">Total</th>
            <th className="p-2 text-center text-xs uppercase">Status</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr><td colSpan={6} className="p-3 text-center text-sm text-[#5B5C5F]">No invoices for this account</td></tr>
          )}
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="p-2 text-sm">{fmtDate(l.invoice_date)}</td>
              <td className="p-2 text-sm">{fmtDate(l.due_date)}</td>
              <td className="p-2 text-sm font-mono-num">{l.invoice_number}</td>
              <td className="p-2 text-sm font-mono-num text-[#5B5C5F]">{l.order_number || "—"}</td>
              <td className="p-2 text-sm text-right font-mono-num text-[#032352]">{currency(l.total)}</td>
              <td className="p-2 text-center">
                {l.paid ? (
                  <span className="text-xs font-medium text-emerald-600">Paid</span>
                ) : l.overdue_days > 0 ? (
                  <span className="text-xs font-medium text-red-600">{l.overdue_days}d overdue</span>
                ) : (
                  <span className="text-xs font-medium text-amber-600">Outstanding</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end mt-6">
        <div className="w-72 flex justify-between items-center pt-3 border-t-2" style={{ borderColor: "#032352" }}>
          <span className="font-heading font-bold text-[#032352]">Total Owed</span>
          <span className="font-mono-num font-bold text-lg text-red-600" data-testid="statement-total-owed">
            {currency(total_owed)}
          </span>
        </div>
      </div>

      <p className="text-center text-xs text-[#5B5C5F] mt-8">
        Please remit payment quoting the invoice number. · J&amp;L Industrial Supplies
      </p>
    </div>
  );
}
